import React, { useState, useEffect, useRef, useContext } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { UserContext } from "../../UserContext";
import axios from "axios";
import {
  FaPlus,
  FaRegCalendarAlt,
  FaMicrophone,
  FaTrash,
  FaStop,
} from "react-icons/fa";
import { IoIosApps } from "react-icons/io";
import "./Problems.css";
import toast from "react-hot-toast";

const formatDateForInput = (timestamp) => {
  if (!timestamp) return "";
  return new Date(timestamp).toISOString().split("T")[0];
};

const ProblemForm = () => {
  const { t } = useTranslation();
  const { problemId } = useParams();
  const navigate = useNavigate();
  const isEditing = !!problemId;
  const { userData, token } = useContext(UserContext);

  const [formData, setFormData] = useState({
    name: "",
    fieldId: "",
    problemDate: formatDateForInput(new Date().getTime()),
    description: "",
    state: "notStarted",
  });

  // START: ADDED STATE TO HOLD THE ORIGINAL PROBLEM DATA IN EDIT MODE
  const [originalProblem, setOriginalProblem] = useState(null);
  // END: ADDED STATE

  const [fields, setFields] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [audioState, setAudioState] = useState({
    status: "idle",
    audioURL: null,
    duration: "0:00",
    error: null,
  });
  const [recordingTime, setRecordingTime] = useState(0);
  const timerIntervalRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const recordStartTsRef = useRef(null);
  const [images, setImages] = useState([]);
  const imageInputRef = useRef(null);

  useEffect(() => {
    const loadForm = async () => {
      if (!userData?.Farm?.id || !token) return;
      const farmId = userData.Farm.id;

      try {
        const fieldsRes = await axios.get(`/api/field/findByFarmId/${farmId}`, {
          headers: { Authorization: token },
        });
        setFields(fieldsRes.data.items || []);

        if (isEditing) {
          const problemRes = await axios.get(`/api/problem/${problemId}`, {
            headers: { Authorization: token },
          });
          const problemData = problemRes.data.items?.[0]?.problem;

          if (problemData) {
            // START: STORE THE FULL ORIGINAL PROBLEM OBJECT
            setOriginalProblem(problemData);
            // END: STORE THE FULL ORIGINAL PROBLEM OBJECT

            setFormData({
              name: problemData.name || "",
              fieldId: problemData.fieldId || "",
              problemDate: formatDateForInput(problemData.problemDate),
              description: problemData.textDesc || "",
              state: problemData.state || "notStarted",
            });
            if (problemData.audioDesc) {
              setAudioState({
                status: "recorded",
                audioURL: problemData.audioDesc,
                duration: "0:00",
                error: null,
              });
            }
            if (problemData.imagesUrls) {
              const existingImages = problemData.imagesUrls
                .split(",")
                .filter((url) => url)
                .map((url) => ({
                  file: null,
                  preview: url.trim(),
                }));
              setImages(existingImages);
            }
          }
        }
      } catch (error) {
        console.error("Failed to load form data", error);
        toast.error(t("Failed to load data."));
      }
    };
    loadForm();
  }, [isEditing, problemId, userData, token, t]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // ... (All other helper functions like formatTime, startRecording, removeImage, etc. remain the same) ...
  const formatTime = (seconds) => {
    if (isNaN(seconds) || seconds === Infinity) return "0:00";
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);
    return `${minutes}:${remainingSeconds.toString().padStart(2, "0")}`;
  };

  const handleMetadataLoad = (e) => {
    const audio = e.target;
    if (audio.duration !== Infinity) {
      const formattedDuration = formatTime(audio.duration);
      setAudioState((prev) => ({ ...prev, duration: formattedDuration }));
    }
  };

  const startRecording = async () => {
    setAudioState({ status: "permission", audioURL: null, error: null });
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      const error = "Media Devices API not supported in this browser.";
      setAudioState({ status: "idle", audioURL: null, error });
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) audioChunksRef.current.push(event.data);
      };

      mediaRecorder.onstop = () => {
        const audioBlob = new Blob(audioChunksRef.current, {
          type: "audio/wav",
        });
        const audioUrl = URL.createObjectURL(audioBlob);
        const approxSeconds =
          (performance.now() - recordStartTsRef.current) / 1000;
        setAudioState({
          status: "recorded",
          audioURL: audioUrl,
          duration: formatTime(approxSeconds),
          error: null,
        });
        stream.getTracks().forEach((track) => track.stop());
      };

      mediaRecorder.start();
      recordStartTsRef.current = performance.now();
      setAudioState((prev) => ({ ...prev, status: "recording" }));
      setRecordingTime(0);
      timerIntervalRef.current = setInterval(() => {
        setRecordingTime((prevTime) => prevTime + 1);
      }, 1000);
    } catch (err) {
      const error = "Microphone access was denied.";
      setAudioState({ status: "idle", audioURL: null, error });
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current?.state === "recording") {
      mediaRecorderRef.current.stop();
      clearInterval(timerIntervalRef.current);
    }
  };

  const handleMicClick = () => {
    if (audioState.status === "idle") startRecording();
    else if (audioState.status === "recording") stopRecording();
  };

  const handleDeleteAudio = () => {
    if (audioState.audioURL?.startsWith("blob:")) {
      URL.revokeObjectURL(audioState.audioURL);
    }
    setAudioState({ status: "idle", audioURL: null, error: null });
  };

  const handleImageFileChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      const files = Array.from(e.target.files);
      const newImages = files.map((file) => ({
        file: file,
        preview: URL.createObjectURL(file),
      }));
      setImages((prev) => [...prev, ...newImages]);
    }
  };

  const removeImage = (index) => {
    const imageToRemove = images[index];
    if (imageToRemove?.preview.startsWith("blob:")) {
      URL.revokeObjectURL(imageToRemove.preview);
    }
    setImages((prev) => prev.filter((_, i) => i !== index));
  };

  useEffect(() => {
    return () => {
      images.forEach((image) => {
        if (image.preview.startsWith("blob:")) {
          URL.revokeObjectURL(image.preview);
        }
      });
    };
  }, [images]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.problemDate || !formData.description) {
      toast.error(t("Please fill out Title, Date, and Description."));
      return;
    }
    setIsSubmitting(true);

    // This payload construction now correctly handles all cases for audio and images
    const problemPayload = {
      // In edit mode, start with all original data to prevent fields from being deleted
      ...(isEditing ? originalProblem : {}),

      // Overwrite with the latest data from the form fields
      name: formData.name,
      textDesc: formData.description,
      fieldId: formData.fieldId ? parseInt(formData.fieldId) : null,
      problemDate: new Date(formData.problemDate).getTime(),
      state: formData.state,
      farmId: userData.Farm.id,
      tenantId: userData.Tenant.id,
      // Add the employeeId from the logged-in user (for new problems)
      // or preserve the original one (for existing problems)
      employeeId: isEditing ? originalProblem.employeeId : userData.Employee.id,

      // BUG FIX & ENHANCEMENT for audio preservation/deletion:
      // The audio URL in the state is the single source of truth.
      // If it's null (deleted), we send null. If it exists, we send the URL.
      audioDesc: audioState.audioURL,

      // Correctly preserve/update the list of images to keep
      imagesUrls: images
        .filter((img) => !img.file) // Keep only URLs of images that were not newly uploaded
        .map((img) => img.preview)
        .join(","),
    };

    if (isEditing) {
      delete problemPayload.createTs;
      delete problemPayload.updateTs;
    }
    if (isEditing) {
      console.log("problem payload in editing", problemPayload);
    } else {
      console.log("problem payload in creating", problemPayload);
    }
    const submissionData = new FormData();
    submissionData.append("problem", JSON.stringify(problemPayload));

    // Append a new audio file only if it's a new recording (a blob)
    if (audioState.audioURL && audioState.audioURL.startsWith("blob:")) {
      const response = await fetch(audioState.audioURL);
      const blob = await response.blob();
      submissionData.append("audioFile", blob, "voice-note.wav");
    }

    // Append only the newly added image files
    const newImageFiles = images.filter((img) => img.file);
    newImageFiles.forEach((imageObject) => {
      submissionData.append("imageFile", imageObject.file);
    });

    const endpoint = isEditing
      ? `api/problem/updateProblemWithAudioAndImages`
      : `api/problem/addProblemWithAudioAndImages`;
    const method = isEditing ? "put" : "post";

    try {
      await axios({
        method,
        url: endpoint,
        data: submissionData,
        headers: {
          "Content-Type": "multipart-form-data",
          Authorization: token,
        },
      });
      toast.success(isEditing ? t("Problem Updated!") : t("Problem Created!"));
      navigate("/problems");
    } catch (error) {
      console.error("Failed to save problem:", error);
      toast.error(t("Failed to save problem."));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="task-form-container">
      <div className="form-card">
        <header className="view-header">
          <h1 className="view-header-title">
            {isEditing ? t("Edit Problem") : t("New Problem")}
          </h1>
        </header>
        <form onSubmit={handleSubmit} className="task-form">
          {/* Form fields remain the same */}
          <div className="problem-form-group">
            <label htmlFor="name">
              {t("Title")} <span style={{ color: "#dc3545" }}>*</span>
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
            />
          </div>

          <div className="problem-form-group">
            <label htmlFor="fieldId">{t("Select Sector")}</label>
            <div className="input-with-icon">
              <IoIosApps className="input-icon" />
              <select
                id="fieldId"
                name="fieldId"
                value={formData.fieldId}
                onChange={handleChange}
              >
                <option value="">{t("All Fields")}</option>
                {fields.map((field) => (
                  <option key={field.id} value={field.id}>
                    {field.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="problem-form-group">
            <label htmlFor="problemDate">
              {t("Problem Date")} <span style={{ color: "#dc3545" }}>*</span>
            </label>
            <div className="input-with-icon">
              <FaRegCalendarAlt className="input-icon" />
              <input
                type="date"
                id="problemDate"
                name="problemDate"
                value={formData.problemDate}
                onChange={handleChange}
                required
              />
            </div>
          </div>

          <div className="problem-form-group">
            <label htmlFor="description">
              {t("Description")} <span style={{ color: "#dc3545" }}>*</span>
            </label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows="4"
              required
            ></textarea>
          </div>

          {/* Audio and Image JSX remains the same */}
          {audioState.status === "recorded" ? (
            <div className="audio-player-wrapper">
              <audio
                key={audioState.audioURL}
                onLoadedMetadata={handleMetadataLoad}
                src={audioState.audioURL}
                controls
                className="audio-player"
              ></audio>
              <span className="audio-duration-display">
                {audioState.duration}
              </span>
              <button
                type="button"
                className="delete-audio-btn"
                onClick={handleDeleteAudio}
              >
                <FaTrash />
              </button>
            </div>
          ) : (
            <div className="audio-recorder-section">
              <button
                type="button"
                className={`mic-button ${
                  audioState.status === "recording" ? "recording" : ""
                }`}
                onClick={handleMicClick}
                disabled={audioState.status === "permission"}
              >
                {audioState.status === "recording" ? (
                  <FaStop />
                ) : (
                  <FaMicrophone />
                )}
              </button>
              {audioState.status === "recording" ? (
                <span className="recording-timer">
                  {formatTime(recordingTime)}
                </span>
              ) : (
                <span>{t("Add problem description with voice recording")}</span>
              )}
            </div>
          )}
          {audioState.error && (
            <p className="error-message">{t(audioState.error)}</p>
          )}

          <div className="problem-form-row attachments-section">
            <input
              type="file"
              accept="image/*"
              ref={imageInputRef}
              onChange={handleImageFileChange}
              style={{ display: "none" }}
              multiple
            />
            <div
              className="image-upload"
              onClick={() => imageInputRef.current.click()}
            >
              <FaPlus />
              <span>{t("Add Images")}</span>
            </div>
          </div>

          <div className="image-previews">
            {images.map((image, index) => (
              <div key={index} className="image-preview-item">
                <img src={image.preview} alt="preview" />
                <button
                  type="button"
                  className="remove-image-btn"
                  onClick={() => removeImage(index)}
                >
                  <FaTrash />
                </button>
              </div>
            ))}
          </div>

          <div className="form-actions">
            <button
              type="button"
              className="button-secondary"
              onClick={() =>
                navigate(isEditing ? `/problems/${problemId}` : "/problems")
              }
            >
              {t("Cancel")}
            </button>
            <button
              type="submit"
              className="button-primary"
              disabled={isSubmitting}
            >
              {isSubmitting
                ? t("Saving...")
                : isEditing
                ? t("Save Changes")
                : t("Save")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProblemForm;
